import { type FC } from "react"

import { type PersonalizedCourseExam } from "@forento/shared/models/course"
import { cn } from "@forento/shared/utilities/tailwind"

import Button from "~/themes/original/components/Button"
import Certificate from "~/themes/original/components/Certificate"
import Checkbox, { Radio } from "~/themes/original/components/Checkbox"
import { CheckmarkIcon, CloseIcon } from "~/themes/original/components/Icon"
import { useTranslation } from "~/translations"
import routes from "~/utilities/routes"

type ExamQuestion = { label: string; options: { id: number; label: string; isCorrect: boolean }[] }

type Props = { exam: Extract<PersonalizedCourseExam, { isCompleted: true }> }
const Result: FC<Props> = ({ exam: { exam, completion, course } }) => {
	const t = useTranslation()

	return (
		<div className="flex flex-col gap-8">
			<div className="flex max-w-4xl flex-col items-center rounded-lg bg-white p-6">
				{completion.score.received >= exam.passScore ? (
					<div className="mb-2 flex flex-col items-center gap-6">
						<div className="bg-success-light h-18 w-18 rounded-full p-4">
							<CheckmarkIcon className="text-success h-full w-full" />
						</div>
						<p className="text-md text-center font-bold">{t("course.exam.passed")}</p>
					</div>
				) : (
					<div className="mb-2 flex flex-col items-center gap-6">
						<div className="bg-danger-light h-18 w-18 rounded-full p-4">
							<CloseIcon className="text-danger h-full w-full" />
						</div>
						<p className="text-md text-center font-bold">{t("course.exam.failed")}</p>
					</div>
				)}
				<p className="mb-2 text-center text-3xl font-bold">
					{completion.score.received}/{completion.score.total}
				</p>
				<p className="text-sm">{t("course.exam.passingScore", { passScore: exam.passScore })}</p>
			</div>
			{exam.certificate && (
				<div className="flex max-w-4xl flex-col items-center gap-4 rounded-lg bg-white p-6">
					<Certificate certificate={{ course, createDate: exam.certificate.createDate }} />
					<Button variant="primary" onClick={routes.certificate.detail(exam.certificate.id)}>
						{t("certificate.goTo")}
					</Button>
				</div>
			)}
			<div className="flex max-w-4xl flex-col items-center gap-4 rounded-lg bg-white p-6">
				{exam.questions.map(question =>
					question.options.filter(x => x.isCorrect).length === 1 ? (
						<SingleChoice key={question.id} question={question} answers={completion.answers.all} />
					) : (
						<MultipleChoice key={question.id} question={question} answers={completion.answers.all} />
					),
				)}
			</div>
		</div>
	)
}

type QuestionProps = { question: ExamQuestion; answers: number[] }
const SingleChoice: FC<QuestionProps> = ({ question, answers }) => (
	<>
		<h2 className="mb-2 text-center text-xl font-bold">{question.label}</h2>
		<div className="mb-6 flex flex-col gap-4">
			{question.options.map(option => (
				<Radio
					key={option.id}
					label={option.label}
					isChecked={answers.includes(option.id)}
					className={cn(option.isCorrect !== answers.includes(option.id) && "text-danger line-through")}
				/>
			))}
		</div>
	</>
)

const MultipleChoice: FC<QuestionProps> = ({ question, answers }) => (
	<>
		<h2 className="mb-2 text-center text-xl font-bold">{question.label}</h2>
		<div className="mb-6 flex flex-col gap-4">
			{question.options.map(option => (
				<Checkbox
					key={option.id}
					label={option.label}
					isChecked={answers.includes(option.id)}
					className={cn(option.isCorrect !== answers.includes(option.id) && "text-danger line-through")}
				/>
			))}
		</div>
	</>
)

export default Result
